<template>
  <base-navigation :min-navigation="minNavigation" :hide-navigation="$route.meta && $route.meta.hideNavigation">
    <template v-slot:bar v-if="!isNavigationBarDisabled">
      <router-view name="navigationBar"></router-view>
      <template v-if="!isNavigationBarProvided">
        <v-flex xs12 md5 xl3 v-if="$user.canAccessModule.OMNISEARCH">
          <fp-omnisearch/>
        </v-flex>
        <v-spacer/>
        <template v-if="$vuetify.breakpoint.mdAndUp">
          <div>
            <v-layout align-center px-2>
              <v-tooltip bottom v-if="$user.canAccessModule.CALENDAR && !$user.isLendiCareChiefWithoutAccessToLoans">
                <template v-slot:activator="{on}">
                  <v-btn v-on="on" text icon @click="goToCalendar" class="fade-icon-button"
                        :data-test="AutomationTestsIds.navigationBar.calendarButton"
                        :class="$onboarding.tours.DASHBOARD.step.CALENDAR">
                    <v-icon>mdi-calendar-outline</v-icon>
                  </v-btn>
                </template>
                <span>{{ $i18n.useNavigation.CALENDAR }}</span>
              </v-tooltip>
              <v-tooltip bottom v-if="$user.canAccessModule.MAIL">
                <template v-slot:activator="{ on }">
                  <v-btn v-on="on"
                        @click="$goto.thirdPartySite($env.getAppDomainConfig().mailDomainUrl, true)"
                        text icon class="fade-icon-button"
                        :data-test="AutomationTestsIds.navigationBar.mailButton"
                        :class="$onboarding.tours.DASHBOARD.step.MAIL">
                    <v-icon>mdi-email-outline</v-icon>
                  </v-btn>
                </template>
                <span>{{ $i18n.useNavigation.MAIL }}</span>
              </v-tooltip>
              <!--Notyfikacje coming soon-->
              <v-tooltip bottom v-if="$user.canAccessModule.NOTIFICATION && !$user.isLendiCareChiefWithoutAccessToLoans">
                <template v-slot:activator="{ on }">
                  <v-row v-on="on">
                    <v-col>
                      <notification-bar v-if="$env.isNotificationOn"/>
                    </v-col>
                  </v-row>
                </template>
                <span>{{ $i18n.useNavigation.NOTIFICATIONS }}</span>
              </v-tooltip>
            </v-layout>
          </div>
          <template v-if="hasAccessToLeads">
            <div :class="$onboarding.tours.DASHBOARD.step.DEALS_CLIENTS_SETTLEMENTS" class="d-inline-flex align-center">
              <v-btn
                v-if="$user.canAccessModule.CLIENT"
                outlined
                :data-test="AutomationTestsIds.newClient"
                class="mr-3"
                @click="() => $hasAccessToNewDealFlow ? openClientModalNew() : openClientModal()">
                {{ $i18n.useNavigation.ADD_CLIENT }}
              </v-btn>
              <v-btn
                v-if="$user.canAccessModule.DEALS"
                color="primary"
                depressed
                @click="() => $hasAccessToNewDealFlow ? createDealNew() : createDeal()"
                class="fade-icon-button"
                :class="$onboarding.tours.LEAD.step.PRESS_NEW_ACTIVITY">{{ $i18n.useNavigation.ADD_DEAL }}</v-btn>
            </div>
          </template>
        </template>
      </template>
    </template>
    <template v-slot:drawer={miniVariant}>
      <v-list ref="navigationListRef" class="pt-0 main-menu hover-placement-list-fix"
              :class="{'hover-list-title': (minNavigation && !miniVariant) || !minNavigation}" dense dark>
        <template v-for="item in lendiMenuItems">
          <list-menu :routeObject="item" :key="item.name"/>
        </template>
        <v-divider/>
        <v-subheader class="px-4 font-weight-medium">
          <span>CRM</span>
        </v-subheader>
        <list-menu v-for="item in crmMenuItems"
                   :routeObject="item"
                   :key="item.name"
                   :class="item.meta.key === DashboardKey.DEALS ? $onboarding.tours.LEAD.step.GO_TO_DEALS : ''"/>
        <lendi-navigation-tile
            v-if="canAccessMyDivisions"
            :title="title"
            :to="myDivisionsRoute"
            icon="mdi-account-group">
        </lendi-navigation-tile>
        <v-divider/>
        <v-subheader v-if="aboutMenuItems.length > 0" class="px-4 font-weight-medium">
          <span>LENDI</span>
        </v-subheader>
        <template v-for="item in aboutMenuItems">
          <list-menu :routeObject="item"
                     :key="item.name"/>
        </template>
        <template v-if="adminMenuItems.length > 0">
          <v-divider/>
          <v-subheader class="px-4 font-weight-medium">
            <span>
              ADMIN
            </span>
          </v-subheader>
          <list-menu v-for="item in adminMenuItems"
                     :routeObject="item"
                     :key="item.name"
          />
        </template>
      </v-list>
    </template>
    <template #user-menu>
      <template v-for="item in userMenuItems">
        <list-menu
            :routeObject="item"
            :key="item.name"
            v-if="item.path.length > 0">
        </list-menu>
      </template>
    </template>
    <template v-slot:default>
      <v-row no-gutters align="start" class="fill-height">
        <v-col class="fill-height router-view-container">
          <router-view :key="$route.params.id"/>
        <FabBtn v-if="showFabMobileBtn" :actionOrFabActions="actions"/>
        <v-avatar v-if="$env.isDev" size="60" color="primary" class="ui-placeholder"/>
        </v-col>
      </v-row>
    </template>
  </base-navigation>
</template>

<script lang="ts">
import {Component, Vue, Watch} from 'vue-property-decorator';
import {AppRoutes} from '@/router';
import {RouteConfig} from 'vue-router';
import {Routes} from '@/router/Routes';
import CreateClient from '@/modules/client/views/create-client/CreateClient.vue';
import ListMenu from '@/modules/ListMenu.vue';
import {ModalPayload} from '@/types/typings';
import Client from '@/models/Client';
import CreateDeal from '@/modules/deals/views/create-deal/CreateDeal.vue';
import BaseNavigation from '@/modules/navigation/BaseNavigation.vue';
import UserButton from '@/modules/navigation/UserButton.vue';
import {Location, Route} from 'vue-router/types/router';
import VersionCheck from '@/services/versionCheck/VersionCheck';
import RefreshApplicationModal from '@/components/commons/RefreshApplicationModal.vue';
import LendiNavigationTile from '@/commons/LendiNavigationTile.vue';
import {UserDivisionStatus} from '@/models/user/UserDTO';
import EnvironmentService from '@/env/EnvironmentService';
import FabBtn from '@/components/commons/NavigationFabBtn.vue';
import {FabAction} from '@/models/interfaces/FabAction';
import {I18NGetter} from '@/services/enumTranslator/I18NGetter';
import {DashboardKey} from '@/modules/onboarding/tours/enums/DashboardKey';
import processProxy from '@/env/processProxy';
import { useNewDealModal } from '@/composables/useNewDealModal';
import { useNewClientModal } from '@/composables/useNewClientModal';
import { useCartModal } from '@/composables/useCartModal';
import { useTour } from '@/composables/useTour';
import { NotificationScope } from '@/modules/notifications/models/NotificationDescriptor';
import { ensureDivisionReportsAccess } from '@/modules/divisionReports/services/DivisionReportsAccess';
import { ComponentPublicInstance } from 'vue';
import { Ref } from 'vue-property-decorator';
import { AutomationTestsIds } from '@/env/AutomationTests/ids/AutomationTestsIds';

const NotificationBar = () => import(/* webpackChunkName: "NotificationBar" */ '@/modules/notification/NotificationBar.vue');
const FpOmnisearch = () => import(/* webpackChunkName: "FpOmnisearch" */ '@/components/inputs/omnisearch/FpOmnisearch.vue');

@Component({
  methods: {I18NGetter,},
  components: {
    FabBtn,
    LendiNavigationTile,
    UserButton,
    BaseNavigation,
    NotificationBar,
    ListMenu,
    FpOmnisearch,
  },
})
export default class NavigationWrapper extends Vue {
  @Ref() readonly navigationListRef: ComponentPublicInstance | undefined;

  readonly AutomationTestsIds = AutomationTestsIds;

  @Watch('$route', { immediate: true, deep: true, })
  public async onUrlChange(newVal: Route) {
    if (this.$env.isProd || this.$env.isPreprod) {
      const childPath = AppRoutes.filter(route => route.meta?.mode).map(child => child.path);
      const searchElement = newVal.path.replace('/app/', '');
      await this.checkVersion(childPath.includes(searchElement));
    }
  }

  ifRouteHaveChildrenWithMetaData(item: RouteConfig): boolean {
    return (item.children?.filter(x => x.meta?.title && x.meta?.icon).length || 0) > 0;
  }

  get DashboardKey() {
    return DashboardKey;
  }

  get myDivisionsRoute(): Location {
    if (this.isMultipleDivisions) {
      return {
        name: Routes.DIVISIONS.DIVISIONS_LIST,
      };
    }
    return {
      name: Routes.DIVISIONS.DIVISIONS_DETAILS,
      // tutaj jest błąd, zakładamy, że zawsze jest przynajmniej jedna dywizja
      params: {id: `${this.myDivisions[0].id}`,},
      query: {previewOnly: 'true',},
    };
  }

  get myDivisions(): UserDivisionStatus[] {
    return this.$user.divisionsChief?.filter(x => x.isMainChief) || [];
  }

  get isMultipleDivisions(): boolean {
    return this.myDivisions.length > 1;
  }

  get title(): string {
    return this.isMultipleDivisions
      ? this.$i18n.usePlatformSection.NEW_TRANSLATION_MY_TEAMS
      : this.$i18n.usePlatformSection.NEW_TRANSLATION_MY_TEAM;
  }

  private isRefreshModalOpen = false;

  private async checkVersion(forceReload: boolean) {
    const isNewVersionAvailable = await VersionCheck.checkBundlesHash(processProxy.VUE_APP_CRM_VERSION_URL!);
    if (isNewVersionAvailable) {
      if (forceReload) {
        window.location.reload();
      } else if (!this.isRefreshModalOpen) {
        this.isRefreshModalOpen = true;
        await this.$modalService.open(RefreshApplicationModal,
          {},
          {maxWidth: 511, persistent: true, fullscreen: undefined, });
        VersionCheck.ApplicationObservable.requireReload = true;
      }
    } else if (this.$env.isPreprodOrDev) {
      // do usunięcia po dłuższych testach
      console.warn('no new version availble');
    }
  }

  public get userMenuItems(): Array<RouteConfig> {
    return AppRoutes
      .filter(route => route.path.length > 0 && route.meta?.mode === 'user')
      .filter(route => !route.meta?.condition || (typeof route.meta.condition === 'function' && route.meta.condition()));
  }

  public get minNavigation() {
    return this.$route.name === Routes.DEALS.KANBAN_DEALS_LIST ||
        this.$route.name === Routes.DEALS.DEAL_DETAILS ||
        window.location.href.includes('calculator') ||
        this.$vuetify.breakpoint.mdOnly ||
        this.$vuetify.breakpoint.smOnly;
  }

  public get showFabMobileBtn() {
    return this.$route.name === Routes.DASHBOARD && this.$vuetify.breakpoint.mdAndDown && this.$user.canAccessModule.DEALS;
  }

  get canAccessMyDivisions(): boolean {
    return this.$user.canAccessModule.MY_DIVISIONS && this.myDivisions.length > 0;
  }
  
  get adminMenuItems(): Array<RouteConfig> {
    return this.menuItemsRoutes('admin');
  }
  
  get aboutMenuItems(): Array<RouteConfig> {
    return this.menuItemsRoutes('about');
  }

  get crmMenuItems(): Array<RouteConfig> {
    return this.menuItemsRoutes('crm');
  }

  get lendiMenuItems(): Array<RouteConfig> {
    return this.menuItemsRoutes('lendi');
  }

  public menuItemsRoutes(key: string): Array<RouteConfig> {
    return AppRoutes
      .filter(route => route.meta && route.path.length > 0 && route.meta?.mode === key &&
        !EnvironmentService.Environment.hasExcludedModule(route) && (!route.meta?.condition || (typeof route.meta.condition === 'function' && route.meta.condition()))
      )
      .map(route => {
        if (route.children) {
          route.children = route.children.filter(child => {
            if (!child.meta?.title) return false;
            return !EnvironmentService.Environment.hasExcludedModule(child) && (!child.meta || !child.meta.condition || (typeof child.meta.condition === 'function' && child.meta.condition()));
          });
        }
        return route;
      });
  }

  public goToCalendar() {
    this.$goto.myCalendar();
  }

  public actions: FabAction[] = [
    {
      icon: 'work',
      method: this.$hasAccessToNewDealFlow ? this.createDealNew : this.createDeal,
      title: this.$i18n.useNavigation.ADD_DEAL,
      routeToNavigate: this.$hasAccessToNewDealFlow ? undefined : 'deals',
    },
    {
      icon: 'account_circle',
      method: this.$hasAccessToNewDealFlow ? this.openClientModalNew : this.openClientModal,
      title: this.$i18n.useNavigation.ADD_CLIENT,
    },
  ];

  public async createDeal() {
    await this.$modalService.open<ModalPayload<null>>(CreateDeal, {},
      {maxWidth: 700, persistent: true, fullscreen: undefined, });
  }

  public async createDealNew() {
    const { openNewDealModal } = useNewDealModal(this.$modalService);
    const modalResponse = await openNewDealModal();

    if (!modalResponse) {
      return;
    }

    if (modalResponse.clientId) {
      const { openCartModal } = useCartModal(this.$modalService);
      await openCartModal(modalResponse.clientId, modalResponse.draftDeals?.[0]?.id);
    } else if (modalResponse.dealId) {
      this.$goto.deal(Number(modalResponse.dealId));
    }
  }

  public async openClientModal() {
    const modalResponse =
      await this.$modalService.open<ModalPayload<Client>>(CreateClient, {},
        {maxWidth: 650, persistent: true, fullscreen: undefined, });
    if (modalResponse && modalResponse.payload) {
      this.$goto.client(modalResponse.payload.id!);
    }
  }

  public async openClientModalNew() {
    const { openAddClientModal } = useNewClientModal(this.$modalService);
    const response = await openAddClientModal({});
    if (response && typeof response === 'object' && 'id' in response && typeof response.id === 'number') {
      this.$goto.client(response.id);
    }
  }

  public get isNavigationBarProvided(): boolean {
    return this.$route.matched.some(route => !!route.components.navigationBar);
  }

  public get isNavigationBarDisabled(): boolean {
    return this.$route.matched.some(route => route.meta?.hideNavigationBar);
  }

  public get hasAccessToLeads(): boolean {
    return !this.$user.isLendiCareUser || (this.$user.isLendiCareUser && this.$user.hasAccessToLoans);
  }

  async mounted() {
    // Deliberately not awaited: nothing below should wait on a cross-origin
    // round-trip. „Raporty" stays hidden until this lands, which is why the
    // route has a guard of its own for anyone arriving by URL.
    ensureDivisionReportsAccess();

    if (this.$user.canAccessModule.BIK && await this.$onboarding.tours.BIK.isEnabled()) {
      useTour(this.navigationListRef!, this.$onboarding.tours.BIK.step.BIK_NAVIGATION);
      await this.$onboarding.tours.BIK.start();
    }
    // Evaluate first-login platform notifications after the (awaited) BIK tour so the two never
    // fire in the same tick. Intentionally outside the BIK access gate — notifications are
    // independent of the tutorial (AC1).
    await this.$platformNotifications.runEligible({ scope: NotificationScope.GLOBAL, });
  }
  
  beforeDestroy() {
    if (this.$onboarding.tours.BIK.isStarted) {
      this.$onboarding.tours.BIK.stop();
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/styles/base/breakpoints/index';

  .main-menu {
    &.v-sheet.v-list {
      background-color: transparent;
    }
    &.hover-placement-list-fix {
      ::v-deep .v-list-group
      .v-list-item__icon:not(.v-list-group__header__append-icon):not(.v-list-group__header__prepend-icon) {
        margin: 8px 8px 16px 8px!important;
      }
      ::v-deep .v-list-group__header {
        justify-content: start;
      }
      ::v-deep .v-list-item {
        padding-left: 25px;
        &__content {
         display: flex!important;
        }

        &__title {
         display: none;
        }
      }
    }
    &.hover-list-title {
      ::v-deep .v-list-item__title {
        display: block!important;
      }
    }
    .list-line-decorator {
      color: rgba(225, 225, 225, $primary-opacity)!important
    }
    ::v-deep .v-list-item:not(.v-list-item--active):not(.v-list-item--disabled) {
      color: var(--token-core-onCore-lowEmphasis) !important;

      &:before {
        background-color: var(--token-overlay-secondaryText-hover) !important;
      }
      &:hover:before {
        opacity: 1 !important;
      }
      
      .v-icon {
        color: var(--token-core-onCore-lowEmphasis) !important
      }
    }

    ::v-deep .v-subheader:not(.v-icon) {
      font-size: 14px!important;
    }
    ::v-deep .v-icon {
      font-size: 22px;
    }
  }

  :deep(.text-bearly-white) {
    color: var(--token-core-onCore-lowEmphasis);
    &.note {
      font-size: 14px;
      align-self: flex-end;
    }
    &.v-list-item--active {
      /* To nie koliduje z darkiem */
      color: #fff !important;
      .v-icon {
        color: #fff !important
      }
    }
  }

  .ui-placeholder {
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 3;
    @include mobile {
      height: 50px !important;
      min-width: 50px !important;
      width: 50px !important;
    }
  }

  .router-view-container {
    @include mobile {
      padding: 0 8px;
    }
  }
</style>
